import { store, bus } from './state.js';
import { maskPhone } from './store.js';
import { formatMoney, formatDateTime, escapeHtml } from './utils.js';
import { DIRECTIONS_BY_ID } from './directionsData.js';
import { DISPUTE_REASONS } from './data.js';

const DISPUTE_WINDOW_MS = 48 * 60 * 60 * 1000;

let onChangeCallback = null;

export function onLeadPanelChange(cb) {
    onChangeCallback = cb;
}

function notifyChange() {
    if (onChangeCallback) onChangeCallback();
}

function statusButtons(lead) {
    const statuses = [
        { id: 'new', label: 'Новая' },
        { id: 'progress', label: 'В работе' },
        { id: 'called', label: 'Дозвонились' },
        { id: 'deal', label: 'Сделка' },
        { id: 'declined', label: 'Отказ клиента' }
    ];
    const disabled = lead.dispute && lead.dispute.status === 'review';
    return statuses.map(s => `
        <button type="button" class="lead-panel__status-btn${lead.status === s.id ? ' active' : ''}" data-status="${s.id}" ${disabled ? 'disabled' : ''}>${s.label}</button>
    `).join('');
}

function disputeBlock(lead) {
    if (lead.dispute) {
        if (lead.dispute.status === 'review') {
            return `<div class="lead-panel__dispute lead-panel__dispute--review">Заявка на проверке — решение придёт в течение 20–45 секунд</div>`;
        }
        if (lead.dispute.result === 'refunded') {
            return `<div class="lead-panel__dispute lead-panel__dispute--approved">Брак подтверждён, деньги возвращены на баланс</div>`;
        }
        return `<div class="lead-panel__dispute lead-panel__dispute--rejected">Брак отклонён: модератор связался с клиентом, запрос целевой</div>`;
    }

    const expired = (Date.now() - lead.createdAt) > DISPUTE_WINDOW_MS;
    if (expired) {
        return `<div class="lead-panel__dispute-expired">Срок для оспаривания прошёл</div>`;
    }
    return `<button type="button" class="lead-panel__dispute-btn" id="lead-dispute-btn">Оспорить заявку</button>`;
}

function historyBlock(lead) {
    const events = [{ label: 'Заявка создана', time: lead.createdAt }];
    if (lead.dispute) {
        events.push({ label: 'Отправлена на проверку', time: lead.dispute.startedAt });
        if (lead.dispute.resolvedAt) {
            events.push({
                label: lead.dispute.result === 'refunded' ? 'Брак подтверждён' : 'Брак отклонён',
                time: lead.dispute.resolvedAt
            });
        }
    }
    return events.map(e => `
        <div class="lead-panel__history-item">
            <span>${e.label}</span>
            <span>${formatDateTime(e.time)}</span>
        </div>
    `).join('');
}

export function renderLeadPanel(leadId) {
    const panel = document.querySelector('#lead-panel');
    const body = document.querySelector('#lead-panel-body');
    const lead = store.getLead(leadId);
    if (!lead) return;

    const direction = DIRECTIONS_BY_ID[lead.directionId];
    const cityConfig = store.cityConfig(lead.cityId);

    body.innerHTML = `
        <div class="lead-panel__head">
            <div>
                <div class="lead-panel__number">Заявка №${lead.number} · ${formatDateTime(lead.createdAt)}</div>
                <h2>${escapeHtml(lead.name)}</h2>
            </div>
            <button type="button" class="lead-panel__close" id="lead-panel-close" aria-label="Закрыть"></button>
        </div>

        <div class="lead-panel__status-badge lead-panel__status-badge--${lead.status}">${lead.dispute && lead.dispute.status === 'review' ? 'На проверке' : statusLabel(lead.status)}</div>

        <div class="lead-panel__row">
            <span>Телефон</span>
            <span>
                ${lead.phoneRevealed
                    ? `<a href="tel:${lead.phone.replace(/\s|-/g, '')}">${lead.phone}</a>`
                    : `${maskPhone(lead.phone)} <button type="button" class="lead-panel__reveal" id="lead-reveal-phone">Показать</button>`}
            </span>
        </div>
        <div class="lead-panel__row"><span>Направление</span><span>${direction ? direction.title : lead.directionId}</span></div>
        <div class="lead-panel__row"><span>Город</span><span>${cityConfig ? cityConfig.name : lead.cityId}</span></div>
        <div class="lead-panel__row"><span>${lead.isRegion ? 'Населённый пункт' : 'Район'}</span><span>${lead.place}</span></div>
        <div class="lead-panel__row"><span>Источник</span><span>${lead.source}</span></div>
        <div class="lead-panel__row"><span>Стоимость</span><span>${formatMoney(lead.cost)}</span></div>

        <div class="lead-panel__comment">${escapeHtml(lead.example)}</div>

        <div class="lead-panel__label">Статус</div>
        <div class="lead-panel__statuses">${statusButtons(lead)}</div>

        ${lead.status === 'deal' ? `
            <div class="lead-panel__field">
                <label for="lead-deal-amount">Сумма сделки</label>
                <input type="number" id="lead-deal-amount" value="${lead.dealAmount || ''}" min="0" step="100">
            </div>
        ` : ''}

        <div class="lead-panel__field">
            <label for="lead-note">Заметка</label>
            <textarea id="lead-note" placeholder="Видна только вам и вашим сотрудникам">${escapeHtml(lead.note || '')}</textarea>
        </div>

        <div class="lead-panel__dispute-wrap">${disputeBlock(lead)}</div>

        <div class="lead-panel__label">История событий</div>
        <div class="lead-panel__history">${historyBlock(lead)}</div>
    `;

    body.querySelector('#lead-panel-close').addEventListener('click', closeLeadPanel);

    const revealBtn = body.querySelector('#lead-reveal-phone');
    if (revealBtn) {
        revealBtn.addEventListener('click', () => {
            store.updateLead(lead.id, { phoneRevealed: true });
            renderLeadPanel(lead.id);
        });
    }

    body.querySelectorAll('.lead-panel__status-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            store.updateLead(lead.id, { status: btn.dataset.status });
            renderLeadPanel(lead.id);
            notifyChange();
        });
    });

    const dealInput = body.querySelector('#lead-deal-amount');
    if (dealInput) {
        dealInput.addEventListener('blur', () => {
            const value = parseInt(dealInput.value, 10) || 0;
            store.updateLead(lead.id, { dealAmount: value });
            notifyChange();
        });
    }

    const noteInput = body.querySelector('#lead-note');
    noteInput.addEventListener('blur', () => {
        store.updateLead(lead.id, { note: noteInput.value });
    });

    const disputeBtn = body.querySelector('#lead-dispute-btn');
    if (disputeBtn) {
        disputeBtn.addEventListener('click', () => openDisputeModal(lead.id));
    }

    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('lead-panel--open'));
}

function statusLabel(status) {
    const map = { new: 'Новая', progress: 'В работе', called: 'Дозвонились', deal: 'Сделка', declined: 'Отказ клиента' };
    return map[status] || status;
}

export function closeLeadPanel() {
    const panel = document.querySelector('#lead-panel');
    panel.classList.remove('lead-panel--open');
    setTimeout(() => { panel.hidden = true; }, 250);
}

document.addEventListener('DOMContentLoaded', () => {
    const overlay = document.querySelector('#lead-panel-overlay');
    if (overlay) overlay.addEventListener('click', closeLeadPanel);
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
            const panel = document.querySelector('#lead-panel');
            if (panel && !panel.hidden) closeLeadPanel();
        }
    });
});

let disputeLeadId = null;

function openDisputeModal(leadId) {
    disputeLeadId = leadId;
    const modal = document.querySelector('#dispute-modal');
    const reasonsEl = document.querySelector('#dispute-reasons');
    const commentEl = document.querySelector('#dispute-comment');
    commentEl.value = '';

    reasonsEl.innerHTML = DISPUTE_REASONS.map((r, i) => `
        <label class="cab-modal__reason">
            <input type="radio" name="dispute-reason" value="${r.id}" ${i === 0 ? 'checked' : ''}>
            <span>${r.label}</span>
        </label>
    `).join('');

    modal.hidden = false;
}

document.addEventListener('DOMContentLoaded', () => {
    const modal = document.querySelector('#dispute-modal');
    if (!modal) return;

    modal.querySelectorAll('[data-close-modal]').forEach(el => {
        el.addEventListener('click', () => { modal.hidden = true; });
    });

    document.querySelector('#dispute-submit').addEventListener('click', () => {
        const reason = modal.querySelector('input[name="dispute-reason"]:checked');
        const comment = modal.querySelector('#dispute-comment').value.trim();
        if (!disputeLeadId) return;

        store.startDispute(disputeLeadId, reason ? reason.value : 'other', comment);
        modal.hidden = true;
        renderLeadPanel(disputeLeadId);
        notifyChange();

        const leadId = disputeLeadId;
        const delay = 20000 + Math.random() * 25000;
        setTimeout(() => {
            const approved = Math.random() < 0.7;
            store.resolveDispute(leadId, approved);
            notifyChange();
            const panel = document.querySelector('#lead-panel');
            if (panel && !panel.hidden) {
                const currentLead = store.getLead(leadId);
                if (currentLead) renderLeadPanel(leadId);
            }
        }, delay);
    });
});

bus.addEventListener('lead:dispute-resolved', () => notifyChange());
